import type { CommerceCartItem } from '@/config/shared/commerce';

export type CommerceStateOptions = {
  /**
   * Quando `false`, `upsertItem` só atualiza itens já presentes
   * (não adiciona novos productIds). Default: `true`.
   */
  readonly allowDynamicItems?: boolean;
};

export type CommerceStateSnapshot = {
  /**
   * Estado vivo do carrinho (runtime).
   * Não confundir com `commerce.cart.initialItems` da config.
   */
  readonly selectedItems: CommerceCartItem[];
  readonly count: number;
};

/**
 * Estado em memória do cart da instância.
 * Inicializado com `config.commerce.cart.initialItems`.
 * Não persiste, não acessa DOM/Transport e não dispara eventos.
 */
export class CommerceState {
  private readonly itemsByProductId = new Map<string, CommerceCartItem>();

  private readonly allowDynamicItems: boolean;

  constructor(
    initialItems: readonly CommerceCartItem[] | undefined = [],
    options: CommerceStateOptions = {}
  ) {
    this.allowDynamicItems = options.allowDynamicItems !== false;
    this.seed(initialItems ?? []);
  }

  getSelectedItems(): CommerceCartItem[] {
    return Array.from(this.itemsByProductId.values()).map(item =>
      this.cloneItem(item)
    );
  }

  hasItem(productId: string): boolean {
    return Boolean(productId) && this.itemsByProductId.has(productId);
  }

  getItem(productId: string): CommerceCartItem | null {
    if (!productId) return null;
    const item = this.itemsByProductId.get(productId);
    return item ? this.cloneItem(item) : null;
  }

  /**
   * Insere ou atualiza item.
   * - Sem `productId` → no-op
   * - `selected === false` → remove se existir / não adiciona
   * - `quantity <= 0` → remove
   * - `allowDynamicItems === false` e item novo → no-op
   */
  upsertItem(item: CommerceCartItem): void {
    if (!item || typeof item !== 'object') return;

    const productId =
      typeof item.productId === 'string' ? item.productId.trim() : '';
    if (!productId) return;

    if (item.selected === false) {
      this.itemsByProductId.delete(productId);
      return;
    }

    const quantity = this.normalizeQuantity(item.quantity);
    if (quantity <= 0) {
      this.itemsByProductId.delete(productId);
      return;
    }

    const exists = this.itemsByProductId.has(productId);
    if (!exists && !this.allowDynamicItems) return;

    const previous = this.itemsByProductId.get(productId);
    const metadata = item.metadata
      ? { ...item.metadata }
      : previous?.metadata
        ? { ...previous.metadata }
        : undefined;

    const next: CommerceCartItem = {
      ...previous,
      ...item,
      productId,
      quantity,
      selected: true,
      ...(metadata ? { metadata } : {}),
    };

    this.itemsByProductId.set(productId, next);
  }

  removeItem(productId: string): void {
    if (!productId) return;
    this.itemsByProductId.delete(productId);
  }

  /**
   * Atualiza quantidade. `quantity <= 0` remove o item.
   */
  updateQuantity(productId: string, quantity: number): void {
    if (!productId || !this.itemsByProductId.has(productId)) return;

    const normalized = this.normalizeQuantity(quantity);
    if (normalized <= 0) {
      this.itemsByProductId.delete(productId);
      return;
    }

    const current = this.itemsByProductId.get(productId)!;
    this.itemsByProductId.set(productId, {
      ...current,
      quantity: normalized,
    });
  }

  clear(): void {
    this.itemsByProductId.clear();
  }

  getSnapshot(): CommerceStateSnapshot {
    const selectedItems = this.getSelectedItems();
    return { selectedItems, count: selectedItems.length };
  }

  private seed(initialItems: readonly CommerceCartItem[]): void {
    for (const item of initialItems) {
      // Seed ignora allowDynamicItems — itens da config sempre entram
      // (exceto selected:false / inválidos / quantity<=0).
      this.seedItem(item);
    }
  }

  private seedItem(item: CommerceCartItem): void {
    if (!item || typeof item !== 'object') return;

    const productId =
      typeof item.productId === 'string' ? item.productId.trim() : '';
    if (!productId) return;
    if (item.selected === false) return;

    const quantity = this.normalizeQuantity(item.quantity);
    if (quantity <= 0) return;

    // Duplicado por productId no seed: mantém o primeiro.
    if (this.itemsByProductId.has(productId)) return;

    this.itemsByProductId.set(productId, {
      ...item,
      productId,
      quantity,
      selected: true,
      ...(item.metadata ? { metadata: { ...item.metadata } } : {}),
    });
  }

  private normalizeQuantity(quantity: number | undefined): number {
    if (quantity === undefined || quantity === null) return 1;
    if (typeof quantity !== 'number' || !Number.isFinite(quantity)) return 1;
    return quantity;
  }

  private cloneItem(item: CommerceCartItem): CommerceCartItem {
    return {
      ...item,
      ...(item.metadata ? { metadata: { ...item.metadata } } : {}),
    };
  }
}
